import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import {
  Button,
  EmptyState,
  Input,
  type ISorted,
  SegmentedControl,
  Table,
} from '@feathr/components';
import { getIconForAction, useDebouncedCallback } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import type { TGoalTab } from '../ReportsConversionsGoalManagerPage.hooks';
import { compareGoalValues, getGoalSortValue } from '../ReportsConversionsGoalManagerPage.utils';
import goalManagerTableColumns from './goalManagerTableColumns';

interface IGoalManagerTableProps {
  filteredGoals: Goal[];
  goals: ListResponse<Goal>;
  search: string;
  setSearch: (search: string) => void;
  setTab: (tab: TGoalTab) => void;
  tab: TGoalTab;
}

function GoalManagerTable({
  filteredGoals,
  goals,
  search,
  setSearch,
  setTab,
  tab,
}: Readonly<IGoalManagerTableProps>): JSX.Element {
  const { t } = useTranslation();
  const [sort, setSort] = useState<ISorted[]>([{ id: 'name' }]);
  const [inputValue, setInputValue] = useState(search);
  const debouncedSetSearch = useDebouncedCallback(setSearch);

  function handleTabChange(newValue: string): void {
    setTab(newValue as TGoalTab);
  }

  function handleSearch(newValue: string = ''): void {
    setInputValue(newValue);
    debouncedSetSearch(newValue);
  }

  const data = [
    { label: t('Active'), value: 'active' },
    { label: t('Archived'), value: 'archived' },
    { label: t('All'), value: 'all' },
  ];

  const tabsNode = (
    <SegmentedControl
      data={data}
      name={'goal-manager-tabs'}
      onChange={handleTabChange}
      value={tab}
    />
  );

  const searchNode = (
    <Input
      onChange={handleSearch}
      placeholder={t('Search by name…')}
      prefix={<FontAwesomeIcon icon={faSearch} />}
      value={inputValue}
    />
  );

  if (goals.isLoaded && goals.models.length === 0) {
    return (
      <EmptyState
        description={t('Get started by creating your first goal.')}
        label={t('No goals yet')}
        theme={'white'}
        width={'full'}
      >
        <Button prefix={getIconForAction('add')} type={'secondary'}>
          {t('Create goal')}
        </Button>
      </EmptyState>
    );
  }

  const sortedGoals = [...filteredGoals].sort((a, b) => {
    for (const s of sort) {
      const cmp = compareGoalValues(
        getGoalSortValue(a, s.id),
        getGoalSortValue(b, s.id),
        s.desc ?? false,
      );
      if (cmp !== 0) {
        return cmp;
      }
    }
    return 0;
  });

  return (
    <Table<Goal>
      columns={goalManagerTableColumns(t)}
      filterElements={[tabsNode, searchNode]}
      filterLabel={<></>}
      getTrProps={(): object => ({ style: { cursor: 'default' } })}
      idKey={'id'}
      isLoading={goals.isPending}
      items={sortedGoals}
      manual={true}
      onSortChange={setSort}
      sort={sort}
    />
  );
}

export default observer(GoalManagerTable);
